import React, { FC } from 'react';
import { View } from 'react-native';
import { CurrentWeatherHeader, CurrentWeatherWidget, PIPWidget } from '@components';
import { useRecoilState } from 'recoil';

import { currentWeatherConditionsPageTestIDs } from '@constants';
import { useContextNodeHandle, useContextReference, useNodeHandle } from '@hooks';
import { AlertsHeadline, screenReaderEnabledState, WeatherState } from '@store';

import { styles } from './styles';

import { VideoContext } from '../../../hooks/useIsVideoFocused';
import { CurrentWeatherConditionsBottomRow } from '../CurrentWeatherConditionsBottomRow/CurrentWeatherConditionsBottomRow';

type CurrentWeatherConditionsPageContentProps = {
  error: unknown;
  isLoading: boolean;
  alerts: AlertsHeadline[];
  currentLocationWeatherData?: WeatherState;
};

export const CurrentWeatherConditionsPageContent: FC<CurrentWeatherConditionsPageContentProps> = ({
  currentLocationWeatherData,
  error = null,
  isLoading = false,
  alerts,
}) => {
  const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);

  const topRowReference = useContextReference<number | null>(currentWeatherConditionsPageTestIDs.currentWeatherTopRow);
  const widgetReference = useContextReference<number | null>(
    currentWeatherConditionsPageTestIDs.currentWeatherConditionsWidget,
  );

  const weatherAlertsHeaderReference = useContextReference<number | null>(
    currentWeatherConditionsPageTestIDs.weatherAlertsHeaderFrame,
  );
  const weatherAlertsHeaderHandle = useNodeHandle(weatherAlertsHeaderReference);
  const topRowHandle = useNodeHandle(topRowReference);
  const widgetHandle = useNodeHandle(widgetReference);

  const alertsListHandle = useContextNodeHandle(currentWeatherConditionsPageTestIDs.alertTilesList);

  return (
    <View>
      <PIPWidget
        height={160}
        pageID={currentWeatherConditionsPageTestIDs.currentWeatherConditionsPage}
        position={'topRight'}
        testID={currentWeatherConditionsPageTestIDs.pipWidget}
        videoContext={VideoContext.currentConditions}
        width={284}
      />
      <View>
        <View style={styles.topRowWrapper}>
          <CurrentWeatherHeader nextFocusDown={widgetReference} />
        </View>
        <View style={styles.middleRowWrapper}>
          <CurrentWeatherWidget
            data={currentLocationWeatherData}
            error={error}
            isLoading={isLoading}
            nextFocusDown={screenReaderEnabled ? weatherAlertsHeaderHandle : alertsListHandle}
            nextFocusUp={screenReaderEnabled ? topRowHandle : null}
            ref={widgetReference}
          />
        </View>
        <CurrentWeatherConditionsBottomRow
          alerts={alerts}
          nextFocusUp={widgetHandle}
          weatherAlertsHeaderHandle={weatherAlertsHeaderHandle}
        />
      </View>
    </View>
  );
};
